import { Autocomplete, Dialog, DialogContent, DialogTitle } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import { IStockMovementInput } from "@/interfaces/sku-movement.interfaces";
import { SkuMovementMessages } from "@/messages/sku/sku-movement.messages";

import { MOVEMENT_TYPE_OPTIONS } from "./constants";
import { buildStockMovementFormSchema, buildStockMovementInitialData } from "./form-data";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: IStockMovementInput) => void;
  messages: SkuMovementMessages;
  stockMovement?: IStockMovementInput | undefined;
}

const StockMovementModal = ({ open, onClose, onSubmit, messages, stockMovement }: Props) => {
  const movementTypeOptions = MOVEMENT_TYPE_OPTIONS(messages);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{messages.stockMovementTitle}</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={buildStockMovementInitialData(stockMovement)}
          validationSchema={buildStockMovementFormSchema(messages)}
          validateOnChange={false}
          onSubmit={(values) => {
            onSubmit(values);
            onClose();
          }}
        >
          {({ values, handleChange, errors, touched, setFieldValue, submitForm }) => {
            return (
              <>
                <MDBox p={3} pb={0}>
                  <MDBox mt={1}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                          fullWidth
                          options={movementTypeOptions}
                          getOptionLabel={(option) => option.label}
                          disableClearable
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          value={
                            movementTypeOptions.find((option) => option.value === values.type) ||
                            undefined
                          }
                          onChange={(_, newValue) => {
                            setFieldValue("type", newValue ? newValue.value : "");
                          }}
                          renderInput={(params) => (
                            <MDInput
                              {...params}
                              label={messages.typeLabel}
                              variant="standard"
                              error={!!errors.type}
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormField
                          label={messages.quantityLabel}
                          name="quantity"
                          type="number"
                          value={values.quantity}
                          onChange={handleChange}
                          error={touched.quantity && Boolean(errors.quantity)}
                          fullWidth
                          variant="standard"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <FormField
                          externalLabel
                          label={messages.commentsLabel}
                          name="comments"
                          value={values.comments}
                          onChange={handleChange}
                          error={touched.comments && Boolean(errors.comments)}
                          fullWidth
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>

                  <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
                    <MDButton variant="gradient" color="light" onClick={onClose}>
                      {messages.cancelButton}
                    </MDButton>

                    <MDButton variant="gradient" color="dark" onClick={submitForm}>
                      {messages.updateButton}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default StockMovementModal;
